import React, { ReactElement } from "react";

import { useMediaQuery } from "../../hooks/use-media-query/useMediaQuery";
import { usePreventBrowserAutoScroll } from "../../hooks/use-prevent-browser-auto-scroll/usePreventBrowserAutoScroll";
import { SortOrder } from "../../utils/sort/SortOrder";
import { ClearFiltersButton } from "../inputs/button/ClearFiltersButton";
import { MultiselectSearchDropdown } from "../inputs/dropdown/MultiselectSearchDropdown";
import { Stack } from "../layouts/stack/Stack";
import { Theme } from "../theme/Theme";
import { ListTable } from "./ListTable";
import { ListTableColumn } from "./ListTableColumn";
import { ListTableSort } from "./ListTableSort";

type SelectedKeys = string[];
type StringDictionary<T> = Partial<
    Record<
        keyof T,
        | string
        | number
        | {
              id: string;
              name: string;
              code: string;
          }[]
    >
>;

interface GenericListTableSort<T extends StringDictionary<T>> extends ListTableSort {
    readonly by: Extract<keyof T, string>;
    readonly order: SortOrder;
}

export interface Option {
    readonly key: string;
    readonly text: string;
}

export interface FilterDropdown {
    readonly options: Option[];
    readonly placeholder: string;
    readonly selectedKeys: SelectedKeys;
    onChange(selectedOptions: Option[]): void;
}

export interface FilteredListTableProps<T extends StringDictionary<T>> {
    readonly filters: {
        readonly filterDropdowns: FilterDropdown[] | Record<string, FilterDropdown>;
        readonly isLoadingFilters: boolean;
        onClearAllFiltersChange(): void;
    };
    readonly table: {
        readonly columns: ListTableColumn<T>[];
        readonly isLoadingItems: boolean;
        readonly items: T[];
        readonly pagination: {
            readonly page: number;
            readonly pageSize: 1 | 10 | 20 | 50;
            readonly totalItems: number;
            onPageChange(page: number): void;
            onPageSizeChange(pageSize: number): void;
        };
        readonly sorting: {
            readonly defaultSort: GenericListTableSort<T>;
            readonly sort: GenericListTableSort<T>;
            onSortChange(sort: GenericListTableSort<T>): void;
        };
    };
}

export const FilteredListTable = <T extends StringDictionary<T>>(
    props: FilteredListTableProps<T>
): ReactElement | null => {
    const { filters, table } = props;
    const { filterDropdowns, isLoadingFilters, onClearAllFiltersChange } = filters;
    const { columns, isLoadingItems, items, pagination, sorting } = table;
    const { onPageChange } = pagination;
    const { defaultSort, sort, onSortChange } = sorting;
    const { neutralQuaternary } = Theme.palette;

    const isLargeScreen = useMediaQuery("(min-width: 634px)");
    const dropdowns = Array.isArray(filterDropdowns) ? filterDropdowns : Object.values(filterDropdowns);
    const isDefaultSort = defaultSort.by === sort.by && defaultSort.order === sort.order;

    let anySelectedKeys = false;

    const renderDropDownFilter = (dropdown: FilterDropdown, index: number): JSX.Element => {
        const { options, placeholder, selectedKeys, onChange } = dropdown;

        const hasOptions = options.length > 0;
        const hasSelectedKeys = selectedKeys.length > 0;

        const minWidth = isLargeScreen ? "260px" : "100%";
        const disabledDropdownStyle: React.CSSProperties | undefined = !hasOptions
            ? { border: `1px solid ${neutralQuaternary}`, borderRadius: "2px", height: "32px", overflow: "hidden" }
            : undefined;

        if (hasSelectedKeys) anySelectedKeys = true;

        return (
            <Stack style={{ ...disabledDropdownStyle, margin: "0px 20px 20px 0px", minWidth }} key={index}>
                <MultiselectSearchDropdown
                    disabled={!hasOptions}
                    onChange={onChange}
                    options={options}
                    placeholder={isLoadingFilters ? "Loading..." : !hasOptions ? `${placeholder} (N/A)` : placeholder}
                    selectedKeys={[...selectedKeys]}
                />
            </Stack>
        );
    };

    const onClearAllFilters = () => {
        onClearAllFiltersChange();
        onSortChange({ by: defaultSort.by, order: defaultSort.order });
        onPageChange(1);
    };

    usePreventBrowserAutoScroll();

    return (
        <>
            <Stack
                style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: "-6px", padding: "6px 12px 0px 12px" }}
            >
                {dropdowns.map(renderDropDownFilter)}
                <Stack style={{ height: "40px", margin: "-4px 0px 0px -10px" }}>
                    <ClearFiltersButton
                        disabled={!anySelectedKeys && isDefaultSort}
                        isLabeled={true}
                        onClear={onClearAllFilters}
                    />
                </Stack>
            </Stack>

            <ListTable
                columns={columns}
                isLoading={isLoadingItems}
                items={items}
                noItemsMessage="No results match your search"
                onSortChange={onSortChange}
                pagination={pagination}
                sort={sort}
            />
        </>
    );
};
